<script setup lang="ts">
import { vAutoAnimate } from '@formkit/auto-animate/vue';
import { computed, nextTick, ref } from 'vue';
import type { LineItemModel } from '../../utils/API/Cart/cartAPISchema';
import { resolveOutOfStockMessage } from '../../utils/checkoutHelpers';
import { scrollIntoViewThenFocus } from '../../utils/scrollHelpers';
import { resolveStockPill } from './resolveStockPill';
import { useClickAndCollectLocationStore } from './stores/clickAndCollectLocationStore';
import { useOrderTypeStore } from './stores/orderTypeStore';
import { useShoppingCartStore } from './stores/shoppingCartStore';

const shoppingCartStore = useShoppingCartStore();
const orderTypeStore = useOrderTypeStore();
const clickAndCollectLocationStore = useClickAndCollectLocationStore();
const summaryElement = ref<HTMLElement | null>(null);

const visible = computed(
  () => shoppingCartStore.stockSummaryVisible && shoppingCartStore.insufficientStockLineItems.length > 0
);

/**
 * Reuses the exact same stock-status copy as the line-item pill
 * (`resolveStockPill`), so the summary and the pill never disagree.
 */
function messageFor(lineItem: LineItemModel): string {
  if (lineItem.currentInventory <= 0) {
    return resolveOutOfStockMessage(orderTypeStore.isClickAndCollect, clickAndCollectLocationStore.storeName);
  }

  const storeName = orderTypeStore.isClickAndCollect
    ? clickAndCollectLocationStore.storeName
    : undefined;
  return resolveStockPill(lineItem.currentInventory, lineItem.quantity, storeName).message;
}

async function focusSummary(): Promise<void> {
  await nextTick();
  if (summaryElement.value) {
    await scrollIntoViewThenFocus(summaryElement.value, { offset: 250 });
  }
}

// Scrolling/focusing is invoked imperatively by the caller of `verifyStock()`
// (the submit handler) via `focusSummary`, exposed below — the same
// ref-and-call pattern `shoppingCart.vue` already uses for
// `ClickAndCollectPanel`. This component never decides *when* to scroll off
// its own reactive state, so a plain cart refetch (e.g. the order-type
// toggle's read-only re-check) can never trigger it by accident.
defineExpose({
  focusSummary,
});
</script>

<template>
  <div
    v-if="visible"
    ref="summaryElement"
    tabindex="-1"
    role="alert"
    class="checkout-form__error-summary"
  >
    <h3>{{ $t('stockSummaryHeading') }}</h3>
    <ul v-auto-animate class="checkout-form__error-list">
      <li
        v-for="lineItem in shoppingCartStore.insufficientStockLineItems"
        :key="lineItem.ean"
        class="checkout-form__error"
      >
        <span class="checkout-form__error-item-name">{{ lineItem.name }}</span>: {{ messageFor(lineItem) }}
      </li>
    </ul>
  </div>
</template>
